import { inject, injectable } from 'inversify'
import { action, makeObservable, observable } from 'mobx'
import { Types } from '@core/ioc/types'
import type { ClockGateway } from '@core/services/clock.gateway'
import { FeedbackConfig, type FeedbackScope, type MessageTone } from './feedback.config'

export type FeedbackChannel = {
  messages: string[]
  tone: MessageTone
}

const EMPTY_CHANNEL: FeedbackChannel = { messages: [], tone: 'success' }

/**
 * Source of truth for the feedback messages surfaced per `FeedbackScope`
 * (a page or form's own message channel). Not `UserScopedState`: feedback is
 * tied to whatever action just ran, not to the signed-in identity, and a
 * stale message surviving into the next session is not the leak class this
 * interface exists to close.
 */
@injectable()
export class FeedbackRepository {
  /**
   * One channel per scope. An observable map rather than the single flat list
   * this used to be, so a Books failure and a login failure can be on screen at
   * once, each in its own place, instead of silently overwriting each other.
   *
   * MobX observable maps track *absent* keys too, so a component reading a
   * scope that has never been written still re-renders when it first is.
   *
   * Shallow (`deep: false`) on purpose: channels are replaced wholesale, never
   * mutated in place, so deep observability buys nothing — and it actively
   * broke the auto-dismiss identity check below, which compares the array it
   * stored against the array it reads back. Deep mode hands back a proxy.
   */
  private channelsPm = observable.map<FeedbackScope, FeedbackChannel>(undefined, { deep: false })

  @inject(FeedbackConfig)
  private feedbackConfig!: FeedbackConfig

  // Auto-dismiss goes through the clock gateway, like NotificationRepository —
  // a timer is nondeterministic, so tests drive it rather than wait on it.
  @inject(Types.IClockGateway)
  private clockGateway!: ClockGateway

  constructor() {
    makeObservable(this, {
      set: action,
      reset: action,
      resetForRouteChange: action,
    })
  }

  channelFor(scope: FeedbackScope): FeedbackChannel {
    return this.channelsPm.get(scope) ?? EMPTY_CHANNEL
  }

  set(scope: FeedbackScope, messages: string[], tone: MessageTone): void {
    this.channelsPm.set(scope, { messages, tone })

    const autoDismissMs = this.feedbackConfig.autoDismissMsFor(scope, tone)
    if (autoDismissMs !== null && messages.length > 0) {
      this.clockGateway.after(autoDismissMs, () => {
        // Only clear what this call put there. A newer message that replaced it
        // owns the channel now and is entitled to its own full lifetime.
        if (this.channelFor(scope).messages === messages) this.reset(scope)
      })
    }
  }

  reset(scope: FeedbackScope): void {
    this.channelsPm.delete(scope)
  }

  resetForRouteChange(): void {
    this.feedbackConfig.scopesClearedOnRouteChange().forEach((scope) => this.channelsPm.delete(scope))
  }
}
